import { Search } from "lucide-react";
import type { FC } from "react";

import { Kbd, KbdGroup } from "@/components/ui/kbd";
import { cn } from "@/lib/utils";

import { useGlobalSearchDialog } from "./GlobalSearchContext";

export const isApplePlatform = (): boolean =>
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad|iPod/.test(navigator.platform ?? "");

/**
 * Input-shaped button in the header's center slot; opens the search palette.
 * Sized like the case title pill (28px, bordered, `text-xs`).
 */
export const GlobalSearchTrigger: FC<{ className?: string }> = ({
  className
}) => {
  const { setOpen } = useGlobalSearchDialog();
  const apple = isApplePlatform();

  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      aria-label="Search or jump to"
      aria-keyshortcuts={apple ? "Meta+K" : "Control+K"}
      className={cn(
        "flex h-7 w-full min-w-0 items-center gap-2 rounded-md border border-border bg-background px-2 text-xs text-muted-foreground select-none",
        "hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none",
        className
      )}
    >
      <Search className="size-3.5 shrink-0" aria-hidden />
      <span className="flex-1 truncate text-left">Search or jump to...</span>
      <KbdGroup className="hidden sm:inline-flex">
        <Kbd className="h-4 min-w-4 px-1 text-xs">{apple ? "⌘" : "Ctrl"}</Kbd>
        <Kbd className="h-4 min-w-4 px-1 text-xs">K</Kbd>
      </KbdGroup>
    </button>
  );
};
